/**
 * AlertsWidget — Generic alerts display (DRR-319/320)
 *
 * YAML-first: No new resolvers. Uses existing queries + constellation config.
 *
 * Two modes:
 * 1. Data-driven: Parent passes `alerts` array directly (preferred)
 * 2. Self-fetch: Parent queries existing resolvers (fhirFlags, notifications)
 *
 * Severity/icons come from:
 * - Backend (via vocabulary/terminology) in the alert data, OR
 * - Config (threshold_ruleset) evaluated by Zen Engine
 *
 * This widget does NOT know about healthcare, finance, or any domain.
 */

import React from 'react';
import { Group, Text, Badge, Stack, Paper, ThemeIcon } from '@mantine/core';
import {
  IconAlertTriangle,
  IconShieldCheck,
  IconInfoCircle,
  IconAlertCircle,
  IconFlag,
} from '@tabler/icons-react';
import { registerWidget, type WidgetProps } from './DashboardWidgetRegistry';

export interface AlertData {
  id: string;
  code?: string;
  label: string;
  message?: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  severityColor?: string;
  category?: string;
  timestamp?: string;
  source?: string;
}

interface AlertsWidgetProps extends WidgetProps {
  alerts?: AlertData[];
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
  info: 'gray',
};

const SEVERITY_ICONS: Record<string, typeof IconAlertTriangle> = {
  critical: IconAlertCircle,
  high: IconAlertTriangle,
  medium: IconFlag,
  low: IconInfoCircle,
  info: IconInfoCircle,
};

function getAlertIcon(severity: string): typeof IconAlertTriangle {
  return SEVERITY_ICONS[severity] ?? IconFlag;
}

function getAlertColor(severity: string, severityColor?: string): string {
  if (severityColor) return severityColor;
  return SEVERITY_COLORS[severity] ?? 'gray';
}

export function AlertsWidget({
  tenantNum,
  subjectActorId,
  config,
  alerts: providedAlerts,
}: AlertsWidgetProps) {
  const severityFilter = config?.severity_filter as string[] | undefined;

  const alerts = React.useMemo(() => {
    if (!providedAlerts) return [];

    if (severityFilter && severityFilter.length > 0) {
      return providedAlerts.filter(a => severityFilter.includes(a.severity));
    }
    return providedAlerts;
  }, [providedAlerts, severityFilter]);

  if (!providedAlerts) {
    return (
      <Text size="xs" c="dimmed">
        Alerts data not provided. Pass alerts prop from parent.
      </Text>
    );
  }

  if (alerts.length === 0) {
    return (
      <Paper p="xs" withBorder radius="sm">
        <Group gap="xs">
          <ThemeIcon color="green" variant="light" size="sm">
            <IconShieldCheck size={14} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">No active alerts</Text>
        </Group>
      </Paper>
    );
  }

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">Active Alerts</Text>

      {alerts.map((alert) => {
        const Icon = getAlertIcon(alert.severity);
        const color = getAlertColor(alert.severity, alert.severityColor);

        return (
          <Paper key={alert.id} p="xs" withBorder radius="sm">
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon color={color} variant="light" size="sm">
                <Icon size={14} />
              </ThemeIcon>
              <Stack gap={0} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>{alert.label}</Text>
                {alert.message && (
                  <Text size="xs" c="dimmed" lineClamp={1}>{alert.message}</Text>
                )}
              </Stack>
              <Badge size="xs" color={color} variant="light">
                {alert.severity.toUpperCase()}
              </Badge>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}

registerWidget('alerts', AlertsWidget, 'core', 'Generic alerts display - pass alerts data from parent');
